<template>
  <div class="header-wrapper" v-show="!$route.path.includes('admin')">
    <!-- Animated gradient mesh background -->
    <div class="gradient-mesh"></div>

    <!-- Floating header capsule -->
    <header class="site-header" :class="{ scrolled: isScrolled }">
      <div class="absolute w-full h-full liquidGlassCard rounded-3xl"></div>
      <div class="header-container relative">
        <!-- Logo -->
        <router-link to="/" class="logo-wrapper">
          <img src="/images/logo.png" alt="Fluxreload" class="logo-img" />
          <span class="logo-text">Fluxreload</span>
        </router-link>

        <!-- Navigation -->
        <nav class="nav-wrapper">
          <!-- Search Bar - hidden on /search page -->
          <div class="search-wrapper" v-show="!$route.path.includes('/search')">
            <form @submit.prevent="handleSearch" class="search-form">
              <svg
                class="search-icon"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="M21 21l-4.35-4.35" />
              </svg>
              <input
                v-model="headerSearchQuery"
                type="text"
                placeholder="Rechercher..."
                class="search-input"
              />
            </form>
          </div>

          <!-- Categories Button -->
          <div class="nav-item-wrapper" ref="categoriesWrapper">
            <button
              @click="toggleDropdown('categories')"
              class="nav-button categories-btn"
              :class="{ active: activeDropdown === 'categories' }"
            >
              <span class="btn-icon">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <rect x="3" y="3" width="7" height="7" rx="1" />
                  <rect x="14" y="3" width="7" height="7" rx="1" />
                  <rect x="3" y="14" width="7" height="7" rx="1" />
                  <rect x="14" y="14" width="7" height="7" rx="1" />
                </svg>
              </span>
              <span class="btn-text categories-text">Catégories</span>
              <svg
                class="chevron categories-chevron"
                :class="{ rotated: activeDropdown === 'categories' }"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2.5"
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>

            <!-- Categories Dropdown -->
            <transition name="dropdown">
              <div
                v-if="activeDropdown === 'categories'"
                class="dropdown-menu categories-dropdown"
              >
                <div class="dropdown-header">
                  <span class="dropdown-title">Explorer</span>
                </div>
                <div class="dropdown-grid">
                  <router-link
                    v-for="cat in categories"
                    :key="cat.key"
                    :to="cat.link"
                    @click="closeDropdown"
                    class="category-card"
                  >
                    <div class="category-icon" v-html="cat.icon"></div>
                    <div class="category-content">
                      <span class="category-name">{{ cat.label }}</span>
                      <span class="category-arrow">→</span>
                    </div>
                  </router-link>
                </div>
              </div>
            </transition>
          </div>
        </nav>

        <!-- Right Actions -->
        <div class="actions-wrapper">
          <!-- Country Selector -->
          <div class="nav-item-wrapper" ref="countryWrapper">
            <button
              @click="toggleDropdown('country')"
              class="nav-button selector-btn"
              :class="{ active: activeDropdown === 'country' }"
            >
              <img
                v-if="currentCountryFlagUrl"
                :src="currentCountryFlagUrl"
                :alt="currentCountryCode"
                class="flag"
              />
              <svg v-else class="flag" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fill-rule="evenodd"
                  d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                  clip-rule="evenodd"
                />
              </svg>
              <span class="code">{{ currentCountryCode }}</span>
            </button>

            <transition name="dropdown">
              <div
                v-if="activeDropdown === 'country'"
                class="dropdown-menu country-dropdown"
              >
                <div class="dropdown-header">
                  <span class="dropdown-title">Pays</span>
                </div>
                <!-- Search Input -->
                <div class="px-4 pb-3">
                  <input
                    v-model="countrySearch"
                    type="text"
                    placeholder="Rechercher un pays..."
                    class="w-full px-4 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-fuchsia-500"
                    ref="countrySearchInput"
                  />
                </div>
                <div class="country-list">
                  <button
                    v-for="country in displayCountries"
                    :key="country.code"
                    @click="selectCountry(country)"
                    class="country-item"
                    :class="{ selected: selectedCountryCode === country.code }"
                  >
                    <img
                      v-if="country.localLogoUrl"
                      :src="country.localLogoUrl"
                      :alt="country.name"
                      class="country-flag"
                    />
                    <svg
                      v-else
                      class="country-flag"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                        clip-rule="evenodd"
                      />
                    </svg>
                    <span class="country-name">{{ country.name }}</span>
                    <svg
                      v-if="selectedCountryCode === country.code"
                      class="check-icon"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2.5"
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </button>
                </div>
              </div>
            </transition>
          </div>

          <!-- Divider -->
          <div class="divider hidden"></div>

          <!-- Currency Selector -->
          <div class="nav-item-wrapper hidden" ref="currencyWrapper">
            <button
              @click="toggleDropdown('currency')"
              class="nav-button selector-btn"
              :class="{ active: activeDropdown === 'currency' }"
            >
              <svg
                class="currency-icon"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <rect x="2" y="5" width="20" height="14" rx="2" />
                <line x1="2" y1="10" x2="22" y2="10" />
              </svg>
              <span class="code">XOF</span>
            </button>

            <transition name="dropdown">
              <div
                v-if="activeDropdown === 'currency'"
                class="dropdown-menu currency-dropdown"
              >
                <div class="dropdown-header">
                  <span class="dropdown-title">Devise</span>
                </div>
                <div class="currency-list">
                  <button
                    v-for="currency in currencies"
                    :key="currency.code"
                    @click="selectCurrency(currency)"
                    class="currency-item"
                    :class="{ selected: selectedCurrency === currency.code }"
                  >
                    <span class="currency-code">{{ currency.code }}</span>
                    <span class="currency-symbol">{{ currency.symbol }}</span>
                    <svg
                      v-if="selectedCurrency === currency.code"
                      class="check-icon"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2.5"
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </button>
                </div>
              </div>
            </transition>
          </div>

          <!-- Divider -->
          <div class="divider"></div>

          <!-- Language Selector -->
          <div class="nav-item-wrapper" ref="languageWrapper">
            <button
              @click="toggleDropdown('language')"
              class="nav-button selector-btn"
              :class="{ active: activeDropdown === 'language' }"
            >
              <svg
                class="lang-icon"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="2" y1="12" x2="22" y2="12" />
                <path
                  d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"
                />
              </svg>
              <span class="code">FR</span>
            </button>

            <transition name="dropdown">
              <div
                v-if="activeDropdown === 'language'"
                class="dropdown-menu language-dropdown"
              >
                <div class="dropdown-header">
                  <span class="dropdown-title">Langue</span>
                </div>
                <div class="language-list">
                  <button
                    v-for="lang in languages"
                    :key="lang.code"
                    @click="selectLanguage(lang)"
                    class="language-item"
                    :class="{ selected: selectedLanguage === lang.code }"
                  >
                    <span class="lang-code">{{ lang.code }}</span>
                    <span class="lang-name">{{ lang.name }}</span>
                    <svg
                      v-if="selectedLanguage === lang.code"
                      class="check-icon"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2.5"
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </button>
                </div>
              </div>
            </transition>
          </div>

          <!-- Help Button -->
          <a href="/help" class="help-btn" style="display: none">
            <span class="help-icon">?</span>
            <span class="help-text">Aide</span>
          </a>
        </div>
      </div>

      <!-- Animated border -->
      <div class="header-border"></div>
    </header>

    <!-- Mobile Floating Search Bar - hidden on /search page -->
    <div
      class="mobile-search-bar"
      :class="{ 'mobile-search-bar-expanded': isMobileSearchExpanded }"
      v-show="!$route.path.includes('/search')"
      @click="openMobileSearch"
    >
      <form @submit.prevent="handleSearch" class="mobile-search-form" @click="handleFormClick">
        <svg
          class="mobile-search-icon"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <circle cx="11" cy="11" r="8" />
          <path d="M21 21l-4.35-4.35" />
        </svg>
        <input
          ref="mobileSearchInput"
          v-model="headerSearchQuery"
          type="text"
          placeholder="Rechercher..."
          class="mobile-search-input"
          @blur="handleMobileSearchBlur"
        />
      </form>
    </div>
  </div>
</template>

<script setup>
import {
  ref,
  computed,
  onMounted,
  onUnmounted,
  watch,
  getCurrentInstance,
  nextTick
} from 'vue';
import { Countries } from '@features/shared/libraries/countries';
import { useRouter } from 'vue-router';

const { proxy } = getCurrentInstance();
const { $stores } = proxy;
const $router = useRouter();

const activeDropdown = ref(null);
const isScrolled = ref(false);
const headerSearchQuery = ref('');
const isMobileSearchExpanded = ref(false);
const mobileSearchInput = ref(null);

// Use country store
const selectedCountryCode = ref(''); // Default to BJ
const selectedCurrency = ref('XOF');
const selectedLanguage = ref('FR');

// Get all countries from Countries library
const allCountriesList = Countries.get();

// Country search
const countrySearch = ref('');
const countrySearchInput = ref(null);

// Display countries (reactive)
const displayCountries = computed(() => {
  const list = [
    { code: '', name: 'Tous les pays', isGlobal: true },
    ...allCountriesList.map((country) => ({
      code: country.countryCode,
      name: country.countryName,
      localLogoUrl: country.localLogoUrl
    }))
  ];

  if (!countrySearch.value || countrySearch.value.trim() === '') {
    return list.slice(0, 50);
  }

  const search = countrySearch.value.toLowerCase().trim();
  return list
    .filter(
      (c) =>
        c.name?.toLowerCase().includes(search) ||
        c.code?.toLowerCase().includes(search)
    )
    .slice(0, 50);
});

// Dropdown refs
const categoriesWrapper = ref(null);
const countryWrapper = ref(null);
const currencyWrapper = ref(null);
const languageWrapper = ref(null);

const categories = [
  {
    key: 'mobile',
    label: 'Recharge mobile',
    link: '/mobile-top-up',
    icon: '<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="5" y="2" width="14" height="20" rx="2"/><path d="M12 18h.01"/></svg>'
  },
  {
    key: 'payment',
    label: 'Cartes de paiement',
    link: '/cards/payment-cards',
    icon: '<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></svg>'
  },
  {
    key: 'entertainment',
    label: 'Divertissement',
    link: '/cards/entertainment-gift-cards',
    icon: '<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="5 3 19 12 5 21 5 3"/></svg>'
  },
  {
    key: 'shopping',
    label: 'Shopping',
    link: '/cards/shopping-gift-cards',
    icon: '<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>'
  },
  {
    key: 'gaming',
    label: 'Jeux vidéo',
    link: '/cards/gaming-cards',
    icon: '<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="6" width="20" height="12" rx="2"/><path d="M6 12h4m-2-2v4"/><path d="M15 13h2"/><path d="M18 11h2"/></svg>'
  },
  {
    key: 'esim',
    label: 'eSim',
    link: '/esim',
    icon: '<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="5" y="2" width="14" height="20" rx="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M12 18h.01" stroke-linecap="round" stroke-linejoin="round"/></svg>'
  }
];

// Computed properties for current country display
const currentCountryCode = computed(() => {
  return selectedCountryCode.value || 'Pays';
});

const currentCountryFlagUrl = computed(() => {
  if (!selectedCountryCode.value) return null;
  const country = allCountriesList.find(
    (c) => c.countryCode === selectedCountryCode.value
  );
  return country?.localLogoUrl || null;
});

const currencies = [
  { code: 'XOF', symbol: 'CFA' },
  { code: 'EUR', symbol: '€' },
  { code: 'USD', symbol: '$' }
];

const languages = [{ code: 'FR', name: 'Français' }];

const toggleDropdown = (name) => {
  if (activeDropdown.value === name) {
    activeDropdown.value = null;
    // Reset search when closing country dropdown
    if (name === 'country') {
      countrySearch.value = '';
    }
  } else {
    activeDropdown.value = name;
    // Focus search input when opening country dropdown
    if (name === 'country') {
      nextTick(() => {
        if (countrySearchInput.value) {
          countrySearchInput.value.focus();
        }
      });
    }
  }
};

const closeDropdown = () => {
  activeDropdown.value = null;
};

const selectCountry = (country) => {
  selectedCountryCode.value = country.code;
  // Update the store
  $stores.global.selectCountry(country.code);
  activeDropdown.value = null;
};

// Watch store changes and update local state
watch(
  () => $stores.global.state.selectedCountry,
  (newCountry) => {
    if (newCountry) {
      selectedCountryCode.value = newCountry.countryCode;
    } else {
      selectedCountryCode.value = '';
    }
  }
);

// Sync initial state from store
onMounted(() => {
  if ($stores.global.state.selectedCountry) {
    selectedCountryCode.value =
      $stores.global.state.selectedCountry.countryCode;
  } else {
    selectCountry(displayCountries.value.find((c) => c.code == 'BJ'));
  }

  window.addEventListener('scroll', handleScroll);
  document.addEventListener('click', handleClickOutside);
});

const selectCurrency = (currency) => {
  selectedCurrency.value = currency.code;
  activeDropdown.value = null;
};

const selectLanguage = (lang) => {
  selectedLanguage.value = lang.code;
  activeDropdown.value = null;
};

// Handle search
const handleSearch = () => {
  if (headerSearchQuery.value.trim()) {
    window.searchQuery = headerSearchQuery.value.trim();
    $router.push('/search');
  }
};

// Open mobile search
const openMobileSearch = () => {
  isMobileSearchExpanded.value = true;
  nextTick(() => {
    mobileSearchInput.value?.focus();
  });
};

// Handle form click - stop propagation when expanded to avoid re-triggering
const handleFormClick = (e) => {
  if (isMobileSearchExpanded.value) {
    e.stopPropagation();
  }
};

// Handle blur on mobile search input
const handleMobileSearchBlur = () => {
  // Delay closing to allow form submission
  setTimeout(() => {
    isMobileSearchExpanded.value = false;
  }, 150);
};

// Handle scroll
const handleScroll = () => {
  isScrolled.value = window.scrollY > 20;
};

// Handle click outside
const handleClickOutside = (e) => {
  const wrappers = [
    categoriesWrapper.value,
    countryWrapper.value,
    currencyWrapper.value,
    languageWrapper.value
  ];

  const clickedOutside = wrappers.every((wrapper) => {
    if (!wrapper) return true;
    return !wrapper.contains(e.target);
  });

  if (clickedOutside) {
    activeDropdown.value = null;
  }
};

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll);
  document.removeEventListener('click', handleClickOutside);
});
</script>

<style scoped>
/* Import distinctive font */
@import url('https://fonts.googleapis.com/css2?family=Work+Sans:wght@400;500;600;700;800;900&display=swap');

/* CSS Variables */
:root {
  --primary-gradient: linear-gradient(
    135deg,
    #01f37a 0%,
    #00d9ff 50%,
    #ff385c 100%
  );
  --glass-bg: rgba(255, 255, 255, 0.85);
  --glass-border: rgba(255, 255, 255, 0.5);
  --shadow-soft: 0 8px 32px rgba(0, 0, 0, 0.08);
  --shadow-hover: 0 16px 48px rgba(0, 0, 0, 0.12);
  --text-primary: #1a1a1a;
  --text-secondary: #666;
  --accent-emerald: #01f37a;
  --accent-fuchsia: #ff385c;
  --accent-cyan: #00d9ff;
}

/* Header Wrapper */
.header-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  padding: 2rem 24px;
}

/* Animated Gradient Mesh Background */
.gradient-mesh {
  position: absolute;
  inset: -50%;
  background:
    radial-gradient(
      ellipse at 20% 30%,
      rgba(1, 243, 122, 0.15) 0%,
      transparent 50%
    ),
    radial-gradient(
      ellipse at 80% 70%,
      rgba(255, 56, 92, 0.12) 0%,
      transparent 50%
    ),
    radial-gradient(
      ellipse at 50% 50%,
      rgba(0, 217, 255, 0.1) 0%,
      transparent 50%
    );
  filter: blur(60px);
  animation: meshMove 20s ease-in-out infinite;
}

@keyframes meshMove {
  0%,
  100% {
    transform: translate(0, 0) scale(1);
  }
  25% {
    transform: translate(5%, -5%) scale(1.05);
  }
  50% {
    transform: translate(-5%, 5%) scale(0.95);
  }
  75% {
    transform: translate(-5%, -5%) scale(1.02);
  }
}

/* Floating Header Capsule */
.site-header {
  position: relative;
  max-width: 1400px;
  margin: 0 auto;
  background: var(--glass-bg);
  border-radius: 24px;
  box-shadow: var(--shadow-soft);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.site-header.scrolled {
  box-shadow: var(--shadow-hover);
  transform: translateY(2px);
}

/* Header Container */
.header-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 24px;
  gap: 32px;
}

/* Animated Border */
.header-border {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: var(--primary-gradient);
  background-size: 300% 100%;
  animation: borderFlow 6s linear infinite;
}

@keyframes borderFlow {
  0% {
    background-position: 0% 50%;
  }
  100% {
    background-position: 300% 50%;
  }
}

/* Logo */
.logo-wrapper {
  display: flex;
  align-items: center;
  text-decoration: none;
  transition: transform 0.3s ease;
  flex-shrink: 0;
}

.logo-wrapper:hover {
  transform: scale(1.02);
}

.logo-img {
  height: 2.5rem;
  width: auto;
  display: block;
}

.logo-text {
  font-family: 'Work Sans', sans-serif;
  font-size: 20px;
  font-weight: 800;
  color: var(--text-primary);
  margin-left: 8px;
  display: block;
}

/* Navigation Wrapper */
.nav-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  justify-content: center;
}

/* Search Wrapper */
.search-wrapper {
  flex: 1;
  max-width: 280px;
}

.search-form {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  background: rgba(26, 26, 26, 0.05);
  border-radius: 14px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.search-form:focus-within {
  background: white;
  box-shadow: 0 0 0 2px rgba(1, 243, 122, 0.2);
}

.search-icon {
  flex-shrink: 0;
  color: var(--text-secondary);
}

.search-form:focus-within .search-icon {
  color: var(--accent-emerald);
}

.search-input {
  flex: 1;
  border: none;
  background: transparent;
  outline: none;
  font-family: 'Work Sans', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
  min-width: 0;
}

.search-input::placeholder {
  color: var(--text-secondary);
}

/* Nav Item Wrapper (for dropdown positioning) */
.nav-item-wrapper {
  position: relative;
}

/* Nav Button */
.nav-button {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: transparent;
  border: none;
  border-radius: 14px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-family: 'Work Sans', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
}

.categories-btn {
  background: rgba(26, 26, 26, 0.05);
}

.categories-btn:hover {
  background: rgba(26, 26, 26, 0.08);
  transform: translateY(-1px);
}

.categories-btn.active {
  background: rgba(26, 26, 26, 0.95);
  color: white;
}

.selector-btn {
  padding: 10px 14px;
  background: transparent;
}

.selector-btn:hover {
  background: rgba(26, 26, 26, 0.05);
}

.selector-btn.active {
  background: rgba(26, 26, 26, 0.05);
}

/* Button Icon */
.btn-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  color: var(--accent-fuchsia);
  transition: color 0.3s ease;
}

.categories-btn.active .btn-icon {
  color: var(--accent-emerald);
}

/* Chevron */
.chevron {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  color: var(--text-secondary);
}

.chevron.rotated {
  transform: rotate(180deg);
}

.categories-btn.active .chevron {
  color: rgba(255, 255, 255, 0.6);
}

/* Dropdown Menu */
.dropdown-menu {
  position: absolute;
  top: calc(100% + 12px);
  left: 50%;
  transform: translateX(-50%);
  background: white;
  border-radius: 20px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  min-width: 280px;
  z-index: 100;
  overflow: hidden;
}

/* Categories Dropdown */
.categories-dropdown {
  width: 320px;
}

/* Dropdown Header */
.dropdown-header {
  padding: 16px 20px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  background: linear-gradient(
    135deg,
    rgba(1, 243, 122, 0.05) 0%,
    rgba(0, 217, 255, 0.05) 100%
  );
}

.dropdown-title {
  font-family: 'Work Sans', sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: var(--text-primary);
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

/* Dropdown Grid */
.dropdown-grid {
  padding: 12px;
  display: grid;
  gap: 4px;
}

/* Category Card */
.category-card {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  border-radius: 14px;
  text-decoration: none;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  background: transparent;
  border: 1px solid transparent;
}

.category-card:hover {
  background: linear-gradient(
    135deg,
    rgba(1, 243, 122, 0.08) 0%,
    rgba(0, 217, 255, 0.05) 100%
  );
  border-color: rgba(1, 243, 122, 0.2);
  transform: translateX(4px);
}

.category-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  background: linear-gradient(
    135deg,
    rgba(1, 243, 122, 0.15) 0%,
    rgba(0, 217, 255, 0.1) 100%
  );
  border-radius: 12px;
  color: var(--accent-emerald);
  flex-shrink: 0;
}

.category-card:hover .category-icon {
  background: linear-gradient(
    135deg,
    rgba(1, 243, 122, 0.25) 0%,
    rgba(0, 217, 255, 0.2) 100%
  );
}

.category-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: 1;
}

.category-name {
  font-family: 'Work Sans', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
}

.category-arrow {
  color: var(--accent-fuchsia);
  font-size: 18px;
  transition: transform 0.25s ease;
  opacity: 0;
}

.category-card:hover .category-arrow {
  opacity: 1;
  transform: translateX(4px);
}

/* Country/Currency/Language Dropdowns */
.country-dropdown,
.currency-dropdown,
.language-dropdown {
  width: 240px;
}

.country-list,
.currency-list,
.language-list {
  padding: 8px;
  max-height: 300px;
  overflow-y: auto;
}

/* Scrollbar styling */
.country-list::-webkit-scrollbar,
.currency-list::-webkit-scrollbar,
.language-list::-webkit-scrollbar {
  width: 4px;
}

.country-list::-webkit-scrollbar-track,
.currency-list::-webkit-scrollbar-track,
.language-list::-webkit-scrollbar-track {
  background: transparent;
}

.country-list::-webkit-scrollbar-thumb,
.currency-list::-webkit-scrollbar-thumb,
.language-list::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.1);
  border-radius: 4px;
}

/* List Items */
.country-item,
.currency-item,
.language-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 12px;
  background: transparent;
  border: none;
  width: 100%;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: 'Work Sans', sans-serif;
}

.country-item:hover,
.currency-item:hover,
.language-item:hover {
  background: rgba(0, 0, 0, 0.04);
}

.country-item.selected,
.currency-item.selected,
.language-item.selected {
  background: linear-gradient(
    135deg,
    rgba(1, 243, 122, 0.1) 0%,
    rgba(0, 217, 255, 0.08) 100%
  );
}

.country-flag {
  font-size: 20px;
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  object-fit: contain;
  color: #e879f9;
}

.country-name,
.currency-symbol,
.lang-name {
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
  flex: 1;
  text-align: left;
}

.currency-code,
.lang-code {
  font-size: 14px;
  font-weight: 700;
  color: var(--text-primary);
}

.check-icon {
  color: var(--accent-emerald);
  flex-shrink: 0;
}

/* Actions Wrapper */
.actions-wrapper {
  display: flex;
  align-items: center;
  gap: 4px;
}

/* Divider */
.divider {
  width: 1px;
  height: 24px;
  background: linear-gradient(
    to bottom,
    transparent,
    rgba(0, 0, 0, 0.1),
    transparent
  );
  margin: 0 4px;
}

/* Flag and Code */
.flag {
  font-size: 18px;
  width: 24px;
  height: 24px;
  object-fit: contain;
  color: #e879f9;
}

.code {
  font-family: 'Work Sans', sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: var(--text-primary);
}

/* Icons */
.currency-icon,
.lang-icon {
  color: var(--text-secondary);
  transition: color 0.3s ease;
}

.selector-btn:hover .currency-icon,
.selector-btn:hover .lang-icon {
  color: var(--text-primary);
}

/* Help Button */
.help-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  background: transparent;
  color: var(--text-primary);
  border-radius: 14px;
  text-decoration: none;
  font-family: 'Work Sans', sans-serif;
  font-size: 14px;
  font-weight: 700;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.help-btn:hover {
  transform: translateY(-2px);
  background: rgba(26, 26, 26, 0.05);
}

.help-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  background: rgba(26, 26, 26, 0.08);
  border-radius: 50%;
  font-size: 12px;
  font-weight: 800;
  color: var(--text-primary);
}

.help-text {
  display: none;
}

@media (min-width: 768px) {
  .help-text {
    display: block;
  }
}

/* Mobile Floating Search Bar */
.mobile-search-bar {
  display: none;
}

@media (max-width: 1024px) {
  .mobile-search-bar {
    display: block;
    position: fixed;
    top: calc(3rem + 80px);
    right: 1rem;
    left: auto;
    z-index: 999;
    width: auto;
  }

  .mobile-search-form {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px;
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(20px);
    border-radius: 20px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
    width: 50px;
    transition: width 0.3s ease;
    cursor: pointer;
  }

  .mobile-search-bar-expanded .mobile-search-form {
    width: 320px;
    padding: 14px 18px;
    cursor: text;
  }

  .mobile-search-icon {
    flex-shrink: 0;
    color: var(--accent-emerald);
  }

  .mobile-search-input {
    flex: 1;
    border: none;
    background: transparent;
    outline: none;
    font-family: 'Work Sans', sans-serif;
    font-size: 15px;
    font-weight: 500;
    color: var(--text-primary);
    width: 0;
    opacity: 0;
    transition: all 0.3s ease;
    overflow: hidden;
  }

  .mobile-search-bar-expanded .mobile-search-input {
    width: auto;
    opacity: 1;
  }

  .mobile-search-input::placeholder {
    color: var(--text-secondary);
  }
}

@media (max-width: 768px) {
  .mobile-search-bar {
    top: calc(3rem + 70px);
  }
}

/* Dropdown Transitions */
.dropdown-enter-active,
.dropdown-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  transform-origin: top center;
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(-8px) scale(0.95);
}

.dropdown-enter-to,
.dropdown-leave-from {
  opacity: 1;
  transform: translateX(-50%) translateY(0) scale(1);
}

/* Responsive */
@media (max-width: 1024px) {
  .header-wrapper {
    padding: 3rem 16px;
  }

  .header-container {
    padding: 10px 16px;
    gap: 16px;
  }

  .logo-wrapper {
    min-width: 140px;
  }

  .logo-img {
    height: 2.5rem;
  }

  .search-wrapper {
    display: none;
  }

  .actions-wrapper {
    gap: 2px;
  }

  .divider {
    display: none;
  }

  .help-btn {
    padding: 10px 14px;
  }

  .categories-text,
  .categories-chevron {
    display: none;
  }

  /* Fix dropdown positioning on mobile */
  .nav-item-wrapper {
    position: static;
  }

  .dropdown-menu {
    left: 0;
    right: 0;
    transform: none;
    width: 100%;
    max-width: none;
    border-radius: 0 0 20px 20px;
    top: calc(100% + 4px);
  }

  .dropdown-enter-from,
  .dropdown-leave-to {
    opacity: 0;
    transform: translateY(-8px) scale(0.95);
  }

  .dropdown-enter-to,
  .dropdown-leave-from {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@media (max-width: 768px) {
  .site-header {
    border-radius: 20px;
  }

  .header-container {
    padding: 8px 12px;
  }

  .logo-wrapper {
    min-width: 120px;
  }

  .logo-img {
    height: 24px;
  }
}
</style>
